// @flow
import React, { Fragment, Component } from 'react';
import ProtectedBy from './ProtectedBy';
import Languages from './Languages';

import {
    MY_ACCOUNT_FORM,
    LOGIN_FORM,
    PURCHASE_HISTORY_FORM,
} from '../../../../constants/forms';
import i18n from '../../../../i18n/i18n.js';

type Props = {
    isVisible: boolean,
    isAuthenticated: boolean,
    language: String,
    branding: {
        paywall_primary_color: string,
    },
    form: string,
    setLanguage: Function,
    handleMoreClick: Function,
    logoutUser: Function,
    options: {
        footerLinks: Array<Object>,
        hideFooterLinks: boolean,
        hideProtectedBy: boolean,
    },
    changeForm: Function,
};

type State = {
    lang: string,
    activeLog: boolean,
    activePrice: boolean,
    activePay: boolean,
};

export default class Footer extends Component<Props, State> {
    state = {
        lang: '',
        activeLog: false,
        activePrice: false,
        activePay: false,
    };

    defaultFooterLinks = [
        {
            text: 'modal_privacy',
            options: {
                href: 'https://inplayer.com/privacy-policy/',
                target: '_blank',
                rel: 'noopener noreferrer',
            },
        },
        {
            text: 'modal_terms',
            options: {
                href: 'https://inplayer.com/terms/',
                target: '_blank',
                rel: 'noopener noreferrer',
            },
        },
        {
            text: 'modal_faq',
            options: {
                href: 'https://inplayer.com/paywall-faq/',
                target: '_blank',
                rel: 'noopener noreferrer',
                className: 'faq',
            },
        },
        {
            text: 'modal_protected',
            options: {
                target: '_blank',
                rel: 'noopener noreferrer',
                href: 'https://inplayer.com/paywall-protected/',
            },
        },
    ];

    authenticatedLinks = [
        {
            text: 'logout',
            options: {
                href: '',
                className: 'logout',
                role: 'button',
                onClick: e => {
                    e.preventDefault();
                    this.handleLogout();
                },
            },
        },
        {
            text: 'details',
            options: {
                href: '',
                role: 'button',
                onClick: e => {
                    e.preventDefault();
                    this.handleMoreClick(MY_ACCOUNT_FORM);
                },
            },
        },
        {
            text: 'purchases_link',
            options: {
                href: '',
                role: 'button',
                onClick: e => {
                    e.preventDefault();
                    this.handleMoreClick(PURCHASE_HISTORY_FORM);
                },
            },
        },
    ];

    componentDidMount() {
        this.setState({ lang: this.props.language });
        i18n.on('languageChanged', this.handleLanguage);
    }

    UNSAFE_componentWillReceiveProps(nextProps: Props) {
        this.setState({ lang: nextProps.language });

        if (nextProps.form === 'LOGIN_FORM') {
            this.setState({
                activeLog: true,
                activePrice: false,
                activePay: false,
            });
        } else if (nextProps.form === 'PRICE_OPTS_FORM') {
            this.setState({
                activeLog: true,
                activePrice: true,
                activePay: false,
            });
        } else if (nextProps.form === 'PAYMENTS_FORM') {
            this.setState({
                activeLog: true,
                activePrice: true,
                activePay: true,
            });
        }
    }

    componentWillUnmount() {
        i18n.off('languageChanged', this.handleLanguage);
    }

    handleLanguage = lang => {
        this.setState({ lang }, () => this.props.setLanguage(lang));
    };

    handleLogout = () => {
        this.props.logoutUser();
        this.props.changeForm(LOGIN_FORM);
    };

    handleMoreClick = form => {
        this.props.handleMoreClick();

        if (form) {
            this.props.changeForm(form);
        }
    };

    renderFooterLinks = () => {
        // render nothing if the user has turned off the footer links feature
        const { hideFooterLinks, footerLinks } = this.props.options;

        if (hideFooterLinks) return null;

        if (footerLinks && footerLinks.length > 0) {
            // render client provided footer links
            return footerLinks.map(link => (
                <a
                    key={link.text}
                    target="_blank"
                    href={link.url}
                    rel="noopener noreferrer"
                >
                    {link.text}
                </a>
            ));
        } else {
            const { lang } = this.state;
            // render default footer links
            return this.defaultFooterLinks.map(link => (
                <a key={link.text} {...link.options}>
                    {i18n.t(`${link.text}`, { lang })}
                </a>
            ));
        }
    };

    renderAuthenticatedLinks = () => {
        const { isAuthenticated } = this.props;

        if (!isAuthenticated) return null;

        const { lang } = this.state;

        return this.authenticatedLinks.map(link => (
            <a key={link.text} {...link.options}>
                {i18n.t(`${link.text}`, { lang })}
            </a>
        ));
    };

    renderMoreButton = () => {
        const { options, isAuthenticated } = this.props;

        if (options.hideFooterLinks && !isAuthenticated) return null;

        const lng = this.state.lang;

        return (
            <a
                href="#"
                data-dropdown="emitDropdown"
                onClick={() => this.handleMoreClick(null)}
            >
                {i18n.t('modal_more', { lng })}
            </a>
        );
    };

    render() {
        const { hideProtectedBy } = this.props.options;

        let progress1;
        let progress2;
        let progress3;

        if (
            this.state.activeLog &&
            !this.state.activePrice &&
            !this.state.activePay
        ) {
            progress1 = this.props.branding.paywall_primary_color
                ? this.props.branding.paywall_primary_color
                : '#00aae6';
        } else if (
            this.state.activeLog &&
            this.state.activePrice &&
            !this.state.activePay
        ) {
            progress1 = this.props.branding.paywall_primary_color
                ? this.props.branding.paywall_primary_color
                : '#00aae6';
            progress2 = this.props.branding.paywall_primary_color
                ? this.props.branding.paywall_primary_color
                : '#00aae6';
        } else if (
            this.state.activeLog &&
            this.state.activePrice &&
            this.state.activePay
        ) {
            progress1 = this.props.branding.paywall_primary_color
                ? this.props.branding.paywall_primary_color
                : '#00aae6';
            progress2 = this.props.branding.paywall_primary_color
                ? this.props.branding.paywall_primary_color
                : '#00aae6';
            progress3 = this.props.branding.paywall_primary_color
                ? this.props.branding.paywall_primary_color
                : '#00aae6';
        }

        return (
            <Fragment>
                {this.props.form === 'LOGIN_FORM' ||
                this.props.form === 'REGISTER_FORM' ||
                this.props.form === 'PRICE_OPTS_FORM' ||
                this.props.form === 'PAYMENTS_FORM' ? (
                    <div id="inplayer-progressbar">
                        <div
                            style={{ backgroundColor: progress1 }}
                            className="progress"
                        />
                        <div
                            style={{ backgroundColor: progress2 }}
                            className="progress"
                        />
                        <div
                            style={{ backgroundColor: progress3 }}
                            className="progress"
                        />
                    </div>
                ) : null}
                <Languages
                    lng={this.state.lang}
                    handleLanguage={this.handleLanguage}
                />
                <footer>
                    <div className="footerlinks">
                        {this.renderMoreButton()}
                        <div
                            className={
                                'inplayer_dropdown_menu dropdown--menu direction-top remove-box-shadow ' +
                                (this.props.isVisible
                                    ? 'dropdown--visible'
                                    : '')
                            }
                        >
                            <div className="dropdown_container">
                                <div className="dropdown_item">
                                    <div className="authenticatedLinks">
                                        {this.renderAuthenticatedLinks()}
                                    </div>
                                    {this.renderFooterLinks()}
                                </div>
                            </div>
                        </div>
                    </div>
                    {!hideProtectedBy && <ProtectedBy lng={this.state.lang} />}
                </footer>
            </Fragment>
        );
    }
}



// WEBPACK FOOTER //
// ./src/ui/components/Modal/Footer/index.js